import React, {
  createContext, JSX, ReactNode, useCallback,
  useContext, useEffect, useMemo, useState
} from 'react'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import { EventSummary } from 'types/api'
import listEventsQuery from 'graphql/listEvents.graphql'

export interface EventsListContextType {
  events: EventSummary[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  prependEvent: (event: EventSummary) => void
  patchEvent: (eventId: string, patch: Partial<EventSummary>) => void
  removeEvent: (eventId: string) => void
  currentStream: CurrentStreamSlot | null
  setCurrentStream: (slot: CurrentStreamSlot) => void
  clearCurrentStream: (eventId: string) => void
}

export interface CurrentStreamSlot {
  event: EventSummary
  viewerIsMember: boolean
}

const summaryOf = (e: EventSummary): EventSummary => ({
  eventId: e.eventId,
  slug: e.slug,
  name: e.name,
  description: e.description,
  status: e.status,
  createdAt: e.createdAt,
  startedAt: e.startedAt,
  endedAt: e.endedAt
})

const sameSlot = (a: CurrentStreamSlot | null, b: CurrentStreamSlot): boolean =>
  a != null &&
  a.viewerIsMember === b.viewerIsMember &&
  a.event.eventId === b.event.eventId &&
  a.event.slug === b.event.slug &&
  a.event.name === b.event.name &&
  a.event.description === b.event.description &&
  a.event.status === b.event.status &&
  a.event.createdAt === b.event.createdAt &&
  a.event.startedAt === b.event.startedAt &&
  a.event.endedAt === b.event.endedAt

const defaultContext: EventsListContextType = {
  events: [],
  loading: false,
  error: null,
  refetch: async () => {},
  prependEvent: () => {},
  patchEvent: () => {},
  removeEvent: () => {},
  currentStream: null,
  setCurrentStream: () => {},
  clearCurrentStream: () => {}
}

const EventsListContext = createContext<EventsListContextType>(defaultContext)
EventsListContext.displayName = 'Events List Context'

export const EventsListProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { auth0Token } = useAuth()
  const [events, setEvents] = useState<EventSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    if (auth0Token === undefined) return
    setLoading(true)
    setError(null)
    try {
      const { events: fetched } = await graphqlRequest<{ events: EventSummary[] }>(
        listEventsQuery, { mineOnly: true }, auth0Token
      )
      setEvents([...fetched].sort((a, b) => b.createdAt.localeCompare(a.createdAt)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load streams')
    } finally {
      setLoading(false)
    }
  }, [auth0Token])

  useEffect(() => { void refetch() }, [refetch])

  const prependEvent = useCallback((event: EventSummary) => {
    setEvents((prev) => {
      if (prev.some((e) => e.eventId === event.eventId)) return prev
      return [event, ...prev]
    })
  }, [])

  const patchEvent = useCallback((eventId: string, patch: Partial<EventSummary>) => {
    setEvents((prev) => prev.map((e) => e.eventId === eventId ? { ...e, ...patch } : e))
  }, [])

  const removeEvent = useCallback((eventId: string) => {
    setEvents((prev) => prev.filter((e) => e.eventId !== eventId))
  }, [])

  const [currentStream, setCurrentStreamState] = useState<CurrentStreamSlot | null>(null)

  const setCurrentStream = useCallback((slot: CurrentStreamSlot) => {
    setCurrentStreamState((prev) => sameSlot(prev, slot)
      ? prev
      : { event: summaryOf(slot.event), viewerIsMember: slot.viewerIsMember })
  }, [])

  const clearCurrentStream = useCallback((eventId: string) => {
    setCurrentStreamState((prev) => prev != null && prev.event.eventId === eventId ? null : prev)
  }, [])

  const value = useMemo<EventsListContextType>(() => ({
    events,
    loading,
    error,
    refetch,
    prependEvent,
    patchEvent,
    removeEvent,
    currentStream,
    setCurrentStream,
    clearCurrentStream
  }), [events, loading, error, refetch, prependEvent, patchEvent, removeEvent, currentStream, setCurrentStream, clearCurrentStream])

  return (
    <EventsListContext.Provider value={value}>
      {children}
    </EventsListContext.Provider>
  )
}

export const useEventsList = (): EventsListContextType =>
  useContext(EventsListContext)
